import { EndpointCallParams } from '@core/api/endpointCallParams';
import { IApiStore } from '@core/api/apiStore';

export type PairingCodeParams = {
    code: string;
};

export type ApprovePairingParams = PairingCodeParams & {
    // Omitted when signing in your own device. Set only by an administrator or manager pairing
    // a device for somebody else; the server re-checks that they are allowed to.
    userId?: string;
};

export type PairableUser = {
    id: string;
    email: string;
    fullName: string;
    firstName: string;
    lastName: string;
    department: string;
    roles: { id: string, name: string }[];
};

// Mirrors DevicePairingStatus on the identity service.
export const PAIRING_STATUS_ACTIVE = 'Consumed';
export const PAIRING_STATUS_REVOKED = 'Revoked';
export const PAIRING_STATUS_SIGNED_OUT = 'SignedOut';
export const PAIRING_STATUS_SUPERSEDED = 'Superseded';
export const PAIRING_STATUS_EXPIRED = 'Expired';
export const PAIRING_STATUS_DENIED = 'Denied';

export type ManagedPairedDevice = PairedDevice & {
    // What the headset reports for itself, stable across pairings of the same device.
    deviceIdentifier: string | null;
    // Null for a request that was denied or expired before anyone claimed it: there is a device
    // in the row, but no person.
    userId: string | null;
    userFullName: string | null;
    userEmail: string | null;
    approvedByUserId: string | null;
    approvedByFullName: string | null;
    pairedOnBehalf: boolean;
    status: string;
    isActive: boolean;
    endedAt: string | null;
};

export type PairedDeviceSummary = {
    total: number;
    active: number;
    revoked: number;
    signedOut: number;
    superseded: number;
    expired: number;
    denied: number;
    pending: number;
};

export type PagedMeta = {
    totalEntities: number;
    totalPages: number;
    currentPage: number;
    limit: number;
    isFirstPage: boolean;
    isLastPage: boolean;
};

export type ManagedDevicesQuery = {
    limit: number;
    page: number;
    searchText: string;
    userId?: string;
    // Empty/omitted means the live pairings only. Ignored by the server for callers who are not
    // allowed to look past them.
    statuses?: string[];
    sortFieldName?: string;
    sortDirection?: number;
};

export type PairedDevice = {
    id: string;
    deviceName: string;
    devicePlatform: string;
    applicationVersion: string;
    pairedFromIp: string;
    pairedAt: string;
};

export type PairingLookupResult = {
    deviceName: string;
    devicePlatform: string;
    applicationVersion: string;
    requestIp: string;
    requestedAt: string;
    expiresAt: string;
};

// Deliberately separate from approve: the page has to show which device is asking before
// anyone can say yes to it.
export const performLookupPairingCode = async (api: IApiStore, params: EndpointCallParams<PairingCodeParams>) => {
    const { payload, ...rest } = params;

    return api.post('identity-app/api/v1/DevicePairing/Lookup', { ...rest, payload });
}

export const performApprovePairing = async (api: IApiStore, params: EndpointCallParams<ApprovePairingParams>) => {
    const { payload, ...rest } = params;

    return api.post('identity-app/api/v1/DevicePairing/Approve', { ...rest, payload });
}

export const performDenyPairing = async (api: IApiStore, params: EndpointCallParams<PairingCodeParams>) => {
    const { payload, ...rest } = params;

    return api.post('identity-app/api/v1/DevicePairing/Deny', { ...rest, payload });
}

export const performGetPairedDevices = async (api: IApiStore, params: EndpointCallParams<{}>) =>
    api.get('identity-app/api/v1/DevicePairing/Devices', { ...params, payload: {} });

export const performRevokePairedDevice = async (api: IApiStore, params: EndpointCallParams<{ id: string }>) => {
    const { payload, ...rest } = params;

    return api.post(`identity-app/api/v1/DevicePairing/Devices/${payload.id}/Revoke`, { ...rest, payload });
}

// ---- administration ----

/**
 * The people this administrator or manager may pair a device for.
 *
 * Deliberately not the Users search: this list is company-wide, where that one narrows a manager
 * to their own groups. Handing somebody a headset is not the same act as managing their account.
 */
export const performGetPairableUsers = async (api: IApiStore, params: EndpointCallParams<{ searchText: string, limit: number, page: number }>) => {
    const { payload, ...rest } = params;

    return api.post('identity-app/api/v1/DevicePairing/Admin/Users', { ...rest, payload });
}

export const performSearchManagedDevices = async (api: IApiStore, params: EndpointCallParams<ManagedDevicesQuery>) => {
    const { payload, ...rest } = params;

    return api.post('identity-app/api/v1/DevicePairing/Admin/Devices', { ...rest, payload });
}

export const performRevokeManagedDevice = async (api: IApiStore, params: EndpointCallParams<{ id: string }>) => {
    const { payload, ...rest } = params;

    return api.post(`identity-app/api/v1/DevicePairing/Admin/Devices/${payload.id}/Revoke`, { ...rest, payload });
}
